var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// log.ts
import log from "loglevel";
import prefix from "loglevel-plugin-prefix";
prefix.reg(log);
log.enableAll();
prefix.apply(log, {
  // [%t]
  template: "%l (%n):",
  levelFormatter(level) {
    return level.toUpperCase();
  },
  nameFormatter(name) {
    return name || "global";
  },
  timestampFormatter(date) {
    return date.toISOString();
  }
});
var Log = log;

// logger.ts
import _ from "lodash";
var LOG_LEVEL = {
  Boot: false,
  AppStore: false,
  AuthStore: false,
  CronStore: false,
  SchemaList: false,
  SchemaObject: false,
  FormulateForm: false
};
var Logger = class {
  static {
    __name(this, "Logger");
  }
  prefix;
  scope;
  constructor(prefix2, scope) {
    this.prefix = prefix2;
    this.scope = scope;
  }
  log(msg, ...params) {
    if (LOG_LEVEL[this.prefix] === false) return;
    console.log(`${this.prefix}${this.scopePrefix}: ${msg}`, ...this.unwrapObservables(params));
  }
  exception(ex, msg, ...params) {
    console.error(`${this.prefix}${this.scopePrefix}: ${msg}`, ...this.unwrapObservables(params));
    console.error(ex);
  }
  error(msg, ...params) {
    console.error(`${this.prefix}${this.scopePrefix}: ${msg}`, ...this.unwrapObservables(params));
  }
  warn(msg, ...params) {
    console.warn(`${this.prefix}${this.scopePrefix}: ${msg}`, ...this.unwrapObservables(params));
  }
  get scopePrefix() {
    const scopeAttrs = _.isFunction(this.scope) ? this.scope() : this.scope || {};
    if (Object.keys(scopeAttrs).length == 0) return "";
    const scope = Object.keys(scopeAttrs).filter((key) => !_.isUndefined(scopeAttrs[key])).map((key) => `${key}=${scopeAttrs[key]}`).join(", ");
    return `(${scope})`;
  }
  unwrapObservables(params) {
    return params.map((p) => {
      if (p === null || p == void 0) return p;
      const o = p;
      if (o.__v_isRef === true) {
        return JSON.parse(JSON.stringify(o.value));
      } else if (o.__ob__) {
        return JSON.parse(JSON.stringify(o));
      } else {
        return p;
      }
    });
  }
};

// i18n.ts
import _2 from "lodash";
var i18n = {
  messages: {},
  locales: [
    "en",
    "de"
  ],
  locale: "en",
  translate(message, data = {}) {
    const path = (data.locale ? data.locale : this.locale).toLowerCase() + "." + message;
    let content = _2.get(this.messages, path) || "";
    content = content.replace(/{[^}]+}/g, (match) => {
      const key = match.substr(1, match.length - 2).trim();
      if (data.hasOwnProperty(key)) return data[key];
      else return key + "?";
    });
    return content;
  },
  t(message, data = {}) {
    return this.translate(message, data);
  }
};

// string.ts
function getStringEqualityRate(s1, s2) {
  let longer = s1;
  let shorter = s2;
  if (s1.length < s2.length) {
    longer = s2;
    shorter = s1;
  }
  let longerLength = longer.length;
  if (longerLength == 0) {
    return 1;
  }
  return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength);
}
__name(getStringEqualityRate, "getStringEqualityRate");
function editDistance(s1, s2) {
  s1 = s1.toLowerCase();
  s2 = s2.toLowerCase();
  let costs = [];
  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2.length; j++) {
      if (i == 0) costs[j] = j;
      else {
        if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) != s2.charAt(j - 1)) newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
    }
    if (i > 0) costs[s2.length] = lastValue;
  }
  return costs[s2.length];
}
__name(editDistance, "editDistance");

// html.ts
var HtmlElement = class _HtmlElement {
  static {
    __name(this, "HtmlElement");
  }
  tag;
  attributes;
  children;
  constructor(tag, attributes, children) {
    this.tag = tag;
    this.attributes = attributes;
    this.children = children;
  }
  append(el) {
    this.children.push(el);
  }
  prepend(el) {
    this.children.unshift(el);
  }
  get html() {
    const o = [];
    let attrs = "";
    for (const a of Object.keys(this.attributes)) {
      attrs += ` ${a}="${this.attributes[a]}"`;
    }
    o.push(`<${this.tag}${attrs}>`);
    for (const child of this.children) {
      if (child instanceof _HtmlElement) {
        o.push(child.html);
      } else {
        o.push(child);
      }
    }
    o.push(`</${this.tag}>`);
    return o.join("\n");
  }
};
function html(tag, attributes, children) {
  return new HtmlElement(tag, attributes || {}, children || []);
}
__name(html, "html");

// helper.ts
import { get } from "lodash";
function commaSeparatedNames(items, selector = "name") {
  if (!items?.length) return "";
  return items.map((item) => {
    if (typeof selector === "function") return selector(item);
    if (typeof item === "string") return item;
    return get(item, selector);
  }).filter((name) => typeof name === "string" && name.length > 0).join(", ");
}
__name(commaSeparatedNames, "commaSeparatedNames");

// storage/storage.helpers.ts
var OFFICE_MIMES = [
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/rtf",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation"
];
var ARCHIVE_MIMES = [
  "application/zip",
  "application/x-zip-compressed",
  "application/x-tar",
  "application/gzip",
  "application/x-gzip",
  "application/x-7z-compressed",
  "application/vnd.rar",
  "application/x-rar-compressed"
];
var TEXT_MIMES = [
  "application/json",
  "application/xml",
  "application/x-yaml"
];
var OFFICE_MIME_TYPES = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation",
  "text/csv"
];
var MIME_BY_EXTENSION = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  odt: "application/vnd.oasis.opendocument.text",
  ods: "application/vnd.oasis.opendocument.spreadsheet",
  odp: "application/vnd.oasis.opendocument.presentation",
  csv: "text/csv"
};
function fileExtension(filename) {
  const name = String(filename || "");
  const dot = name.lastIndexOf(".");
  return dot > -1 ? name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, "") : "";
}
__name(fileExtension, "fileExtension");
function mimeFromFilename(filename) {
  return MIME_BY_EXTENSION[fileExtension(filename)] || null;
}
__name(mimeFromFilename, "mimeFromFilename");
function normalizeMime(mime) {
  return (mime || "").split(";")[0].trim().toLowerCase();
}
__name(normalizeMime, "normalizeMime");
function assetKind(mime) {
  const m = normalizeMime(mime);
  if (!m) return "other";
  if (m === "application/pdf") return "pdf";
  if (m.startsWith("image/")) return "image";
  if (m.startsWith("video/")) return "video";
  if (m.startsWith("audio/")) return "audio";
  if (m.startsWith("text/") || TEXT_MIMES.includes(m)) return "text";
  if (m.startsWith("application/vnd.openxmlformats-officedocument") || OFFICE_MIMES.includes(m)) return "office";
  if (ARCHIVE_MIMES.includes(m)) return "archive";
  return "other";
}
__name(assetKind, "assetKind");
function mimeMatches(mime, patterns) {
  const m = normalizeMime(mime);
  if (!m) return false;
  return (patterns || []).some((raw) => {
    const p = normalizeMime(raw);
    if (p === "*" || p === "*/*") return true;
    if (p.endsWith("/*")) return m.startsWith(p.slice(0, -1));
    return m === p;
  });
}
__name(mimeMatches, "mimeMatches");
function validateAgainstPolicy(policy, file) {
  const errors = [];
  const mime = normalizeMime(file.mimeType);
  const svgExplicit = (policy.mimeTypes || []).some((p) => normalizeMime(p) === "image/svg+xml");
  if (mime === "image/svg+xml" && !svgExplicit) {
    errors.push(`Mime type '${file.mimeType}' is not allowed`);
  } else if (!mimeMatches(file.mimeType, policy.mimeTypes)) {
    errors.push(`Mime type '${file.mimeType}' is not allowed (allowed: ${policy.mimeTypes.join(", ")})`);
  }
  if (!(file.size > 0)) {
    errors.push("File is empty");
  } else if (file.size > policy.maxSize) {
    errors.push(`File size ${file.size} exceeds maximum of ${policy.maxSize} bytes`);
  }
  return errors;
}
__name(validateAgainstPolicy, "validateAgainstPolicy");
function sanitizeSegment(value) {
  return String(value ?? "").toLowerCase().replace(/[^a-z0-9_-]/g, "");
}
__name(sanitizeSegment, "sanitizeSegment");
function buildAssetKey(params) {
  const d = params.date || /* @__PURE__ */ new Date();
  const pad = /* @__PURE__ */ __name((n) => String(n).padStart(2, "0"), "pad");
  const day = `${d.getUTCFullYear()}/${pad(d.getUTCMonth() + 1)}/${pad(d.getUTCDate())}`;
  const ext = String(params.ext ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const base = [
    params.model,
    params.field,
    params.assetId
  ].map(sanitizeSegment).join("/");
  return `${params.prefix || ""}${day}/${base}/original${ext ? "." + ext : ""}`;
}
__name(buildAssetKey, "buildAssetKey");
function contentDispositionHeader(type, filename) {
  const name = String(filename || "file").replace(/[\r\n]/g, " ");
  const fallback = name.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/ß/g, "ss").replace(/[^\x20-\x7e]/g, "_").replace(/["\\]/g, "_").replace(/%/g, "_");
  const encoded = encodeURIComponent(name).replace(/['()*]/g, (c) => "%" + c.charCodeAt(0).toString(16).toUpperCase());
  return `${type}; filename="${fallback}"; filename*=UTF-8''${encoded}`;
}
__name(contentDispositionHeader, "contentDispositionHeader");
var STORAGE_UPLOAD_CREATE_MUTATION = `mutation storage_upload_create($policy: String!, $filename: String!, $mime_type: String!, $size: Int!) {
  storage_upload_create(policy: $policy, filename: $filename, mime_type: $mime_type, size: $size) {
    asset_id
    method
    mode
    url
    fields
    expires_at
  }
}`;
var STORAGE_UPLOAD_COMPLETE_MUTATION = `mutation storage_upload_complete($asset_id: String!) {
  storage_upload_complete(asset_id: $asset_id) {
    id
    collection
    policy
    kind
    status
    original_name
    mime_type
    size
    created_at
    uploaded_at
  }
}`;
function startsWithBytes(bytes, offset, sig) {
  if (bytes.length < offset + sig.length) return false;
  return sig.every((b, i) => bytes[offset + i] === b);
}
__name(startsWithBytes, "startsWithBytes");
function sniffMime(bytes) {
  if (startsWithBytes(bytes, 0, [
    37,
    80,
    68,
    70,
    45
  ])) return "application/pdf";
  if (startsWithBytes(bytes, 0, [
    137,
    80,
    78,
    71,
    13,
    10,
    26,
    10
  ])) return "image/png";
  if (startsWithBytes(bytes, 0, [
    255,
    216,
    255
  ])) return "image/jpeg";
  if (startsWithBytes(bytes, 0, [
    80,
    75,
    3,
    4
  ])) return "application/zip";
  if (startsWithBytes(bytes, 0, [
    208,
    207,
    17,
    224,
    161,
    177,
    26,
    225
  ])) return "application/x-ole-storage";
  if (startsWithBytes(bytes, 0, [
    71,
    73,
    70,
    56
  ])) return "image/gif";
  if (startsWithBytes(bytes, 0, [
    82,
    73,
    70,
    70
  ]) && startsWithBytes(bytes, 8, [
    87,
    69,
    66,
    80
  ])) return "image/webp";
  return null;
}
__name(sniffMime, "sniffMime");
var ZIP_CONTAINER_MIMES = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation"
];
var OLE_CONTAINER_MIMES = [
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint"
];
function isSniffConsistent(declaredMime, sniffedMime) {
  const declared = normalizeMime(declaredMime);
  if (ZIP_CONTAINER_MIMES.includes(declared)) return sniffedMime === "application/zip";
  if (OLE_CONTAINER_MIMES.includes(declared)) return sniffedMime === "application/x-ole-storage";
  const checkable = declared === "application/pdf" || declared.startsWith("image/") && declared !== "image/svg+xml";
  if (!checkable) return true;
  if (!sniffedMime) return false;
  if (declared === "image/jpg") return sniffedMime === "image/jpeg";
  return declared === sniffedMime;
}
__name(isSniffConsistent, "isSniffConsistent");
var MIME_EXTENSIONS = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
  "application/vnd.oasis.opendocument.text": "odt",
  "application/vnd.oasis.opendocument.spreadsheet": "ods",
  "application/vnd.oasis.opendocument.presentation": "odp",
  "text/csv": "csv"
};
function extensionForUpload(filename, mime) {
  const name = String(filename || "");
  const dot = name.lastIndexOf(".");
  if (dot > -1) {
    const ext = name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, "");
    if (ext && ext.length <= 10) return ext;
  }
  return MIME_EXTENSIONS[normalizeMime(mime)] || "bin";
}
__name(extensionForUpload, "extensionForUpload");

// batch.ts
var MAX_LISTED_FAILURES = 5;
var BatchError = class _BatchError extends AggregateError {
  static {
    __name(this, "BatchError");
  }
  failures;
  results;
  constructor(failures, results, total) {
    super(failures.map((f) => f.error), _BatchError.buildMessage(failures, total)), this.failures = failures, this.results = results;
    this.name = "BatchError";
  }
  static buildMessage(failures, total) {
    const listed = failures.slice(0, MAX_LISTED_FAILURES).map((f) => `[${f.index}] ${f.error instanceof Error ? f.error.message : String(f.error)}`);
    const rest = failures.length - listed.length;
    return `${failures.length} of ${total} items failed: ${listed.join("; ")}${rest > 0 ? `; ... and ${rest} more` : ""}`;
  }
};
function settle(results, failures, total) {
  if (failures.length) {
    failures.sort((a, b) => a.index - b.index);
    throw new BatchError(failures, results, total);
  }
  return results;
}
__name(settle, "settle");
async function runInBatches(items, batchSize, fn) {
  const results = new Array(items.length);
  const failures = [];
  const size = Math.max(batchSize, 1);
  for (let i = 0; i < items.length; i += size) {
    const batch = items.slice(i, i + size);
    const settled = await Promise.allSettled(batch.map(async (item, batchIndex) => fn(item, i + batchIndex)));
    settled.forEach((outcome, batchIndex) => {
      const index = i + batchIndex;
      if (outcome.status === "fulfilled") {
        results[index] = outcome.value;
      } else {
        failures.push({
          index,
          item: items[index],
          error: outcome.reason
        });
      }
    });
  }
  return settle(results, failures, items.length);
}
__name(runInBatches, "runInBatches");
async function runWithConcurrency(items, concurrency, fn) {
  const results = new Array(items.length);
  const failures = [];
  let next = 0;
  const worker = /* @__PURE__ */ __name(async () => {
    while (next < items.length) {
      const index = next++;
      try {
        results[index] = await fn(items[index], index);
      } catch (error) {
        failures.push({
          index,
          item: items[index],
          error
        });
      }
    }
  }, "worker");
  const workerCount = Math.min(Math.max(concurrency, 1), items.length);
  await Promise.all(Array.from({
    length: workerCount
  }, worker));
  return settle(results, failures, items.length);
}
__name(runWithConcurrency, "runWithConcurrency");
export {
  BatchError,
  HtmlElement,
  LOG_LEVEL,
  Log,
  Logger,
  MIME_BY_EXTENSION,
  MIME_EXTENSIONS,
  OFFICE_MIME_TYPES,
  STORAGE_UPLOAD_COMPLETE_MUTATION,
  STORAGE_UPLOAD_CREATE_MUTATION,
  assetKind,
  buildAssetKey,
  commaSeparatedNames,
  contentDispositionHeader,
  extensionForUpload,
  getStringEqualityRate,
  html,
  i18n,
  isSniffConsistent,
  mimeFromFilename,
  mimeMatches,
  runInBatches,
  runWithConcurrency,
  sniffMime,
  validateAgainstPolicy
};
//# sourceMappingURL=index.mjs.map